import React from "react";
import {LidGeometry} from "./lidGeometry";
import {Line, DashedLine} from "../../../Renderer/primitives";
import {getFullLabelText} from "../../../../domain/manhole";
import {Group, Rect, Text} from "react-konva";
import {anthracite, concreteGray, FONT_SIZE} from "../../../../../constants";
import {ManholeGeometry} from "./manholeGeometry";
import {Manhole} from "../../../../../types";
import {computeCoveredOutline} from "./computeCoveredOutline";

export const renderLidOutline = (lidGeometry: LidGeometry) => {
    return <Line points={
        [
            lidGeometry.left,
            lidGeometry.top,
            lidGeometry.left,
            lidGeometry.bottom,
            lidGeometry.right,
            lidGeometry.bottom,
            lidGeometry.right,
            lidGeometry.top,
            lidGeometry.left,
            lidGeometry.top
        ]
    }/>
}

export const renderVaultOutlineBehindRects = (
    vaultOutline: {
        x: number;
        w: number;
        y: number;
        h: number;
    },
    rects: {
        x: number;
        w: number;
        y: number;
        h: number;
    }[], renderWithinRectOnly = false
) => {
    const {solid, dashed} = computeCoveredOutline(vaultOutline, rects);

    return <React.Fragment>
        {
            !renderWithinRectOnly &&
            solid.map((s, i) =>
                (<Line points={[s.x1, s.y1, s.x2, s.y2]} key={'solid_vault_outline_' + i}/>)
            )
        }
        {
            dashed.map((s, i) =>
                (<DashedLine points={[s.x1, s.y1, s.x2, s.y2]} key={'dashed_vault_outline_' + i}/>)
            )
        }
    </React.Fragment>
}

export const renderTypePlates = (mg: ManholeGeometry, scale: number, manhole: Manhole) => {
    const renderTypePlate = () => {
        const getXDistance = () => 4.5 * scale
        const getYCenter = () => mg.closureHeight / 2
        const getPlateWidth = () => 140
        const getPlateHeight = () => 2 * FONT_SIZE

        const renderOutline = () => <Line
            points={[0, 0, 0, getPlateHeight(), getPlateWidth(), getPlateHeight(), getPlateWidth(), 0, 0, 0]}/>
        const renderText = () => <Text
            fontSize={FONT_SIZE}
            width={getPlateWidth()}
            align={'center'}
            y={FONT_SIZE / 2 + 1}
            text={getFullLabelText(manhole.type)}
        />

        return (<Group x={getXDistance()} y={getYCenter() + getPlateWidth() / 2} rotation={-90}>
            {renderOutline()}
            {renderText()}
        </Group>)
    }

    return ([mg.left, ...mg.verticalClosureIntersectionsX].map(x => [
        mg.top,
        ...mg.horizontalClosureIntersectionsY
    ].map(y => <Group x={x} y={y} key={'keyhole_' + x + '_' + y}>
        {renderTypePlate()}
    </Group>)))
}

export const renderFillLabel = (fill: string) => {
    const getLabelWidth = () => fill === 'partialfill' || fill === 'without' ? 230 : 100

    const getText = () => fill === 'partialfill' ?
        'Pflästerung, Teilfüllung (Beton)' :
        fill === 'without' ?
            'Pflästerung, ohne Betonfüllung' :
            fill === 'concretegray' ? 'Betongrau' : fill === 'anthracite' ? 'Anthrazit' : ''
    const getColor = () => fill === 'concretegray' ?
        concreteGray :
        fill === 'anthracite' ? anthracite : ''

    const renderIcon = () => {
        const getSideLength = () => 1.5 * FONT_SIZE
        const getTop = () => -getSideLength() / 2
        const getBottom = () => getSideLength() / 2
        const getLeft = () => 0
        const getRight = () => getLeft() + getSideLength()

        const renderSolidColorIcon = () => <Rect
            y={getTop()}
            name="fill"
            width={getSideLength()}
            height={getSideLength()}
            fill={getColor()}
        />

        const renderPavingIcon = () => {
            const getLineCount = () => 3
            const getLineDistance = () => getSideLength() / (getLineCount() + 1)
            const getLinePosition = (i: number) => i * getLineDistance()

            return Array(3)
                .fill(null)
                .reduce((total, _, i) => [...total,
                    <Line points={[getLeft() + getLinePosition(i + 1),
                        getTop(),
                        getLeft() + getLinePosition(i + 1),
                        getBottom()]} key={'horizontal_paving_' + i} strokeWidth={0.3}/>,
                    <Line points={[getLeft(),
                        getTop() + getLinePosition(i + 1),
                        getRight(),
                        getTop() + getLinePosition(i + 1)]} key={'vertical_paving_' + i} strokeWidth={0.3}/>], [])
        }

        return fill === 'partialfill' || fill === 'without' ?
            renderPavingIcon() :
            renderSolidColorIcon()
    }
    const renderText = () => {
        return (<Text
            fontSize={FONT_SIZE}
            width={getLabelWidth()}
            align={'left'}
            x={2 * FONT_SIZE}
            y={-FONT_SIZE / 2 + 1}
            text={getText()}
        />)
    }

    return (<Group y={getLabelWidth() / 2} rotation={-90}>
        {renderIcon()}
        {renderText()}
    </Group>)
}

export const renderHorizontalClosureIntersections = (manholeGeometry: ManholeGeometry) => {
    return manholeGeometry.horizontalClosureIntersectionsY
        .map((p, i) => <Line points={[manholeGeometry.left, p, manholeGeometry.right, p]}
                             key={'vertical_divider_' + i}/>)
}

export const renderVerticalClosureIntersections = (manholeGeometry: ManholeGeometry) => {
    return manholeGeometry.verticalClosureIntersectionsX
        .map((p, i) => <Line
            points={[p, manholeGeometry.top, p, manholeGeometry.bottom]}
            key={'horizontal_divider_' + i}
        />)
}

export const renderVgSplitline = (manholeGeometry: ManholeGeometry) => {
    return manholeGeometry.verticalClosureIntersectionsX
        .map((p, i) => <Line
            points={[p, manholeGeometry.top, p, manholeGeometry.bottom]}
            key={'horizontal_divider_' + i}
        />)
}
